<template>
    <div class="p-3">
        <div class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
            <div>
                <h3 class="mb-0">Riwayat Pelayanan</h3>
                <small class="text-muted">
                    Seluruh registrasi milik satu pasien beserta riwayat pemeriksaannya.
                </small>
            </div>
            <router-link to="/riwayat-pasien" class="btn btn-outline-secondary btn-sm">
                <i class="fa fa-arrow-left"></i> Kembali ke Daftar Pasien
            </router-link>
        </div>
        <hr />

        <div class="card border-0 shadow-sm mb-3 bg-light-subtle">
            <div class="card-body py-3">
                <div class="row g-3 align-items-center">
                    <div class="col-auto">
                        <div class="avatar-circle"><i class="fa fa-user"></i></div>
                    </div>
                    <div class="col">
                        <div class="fw-bold fs-5">{{ pasien.nama || "—" }}</div>
                        <div class="text-muted small d-flex flex-wrap gap-3">
                            <span><i class="fa fa-id-card me-1"></i>No. RM {{ medrec }}</span>
                            <span v-if="pasien.sex"><i class="fa fa-venus-mars me-1"></i>{{ pasien.sex }}</span>
                            <span v-if="pasien.bod">
                                <i class="fa fa-birthday-cake me-1"></i>{{ formatTanggal(pasien.bod) }}
                            </span>
                            <span><i class="fa fa-history me-1"></i>{{ history_data.length }} registrasi</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-white fw-semibold d-flex align-items-center gap-2">
                <i class="fa fa-filter text-success"></i> Pencarian Data
            </div>

            <div class="card-body">
                <div class="row g-3">
                    <div class="col-12 col-lg-6">
                        <label class="form-label small fw-semibold text-muted">Unit Tindakan</label>
                        <div class="btn-group w-100" role="group">
                            <template v-for="unit in unit_tindakan_options">
                                <input
                                    :key="`unit-input-${unit.value}`"
                                    type="radio"
                                    class="btn-check"
                                    name="unit_tindakan"
                                    :id="`unit-${unit.value}`"
                                    :value="unit.value"
                                    v-model="filter.unit_tindakan"
                                />
                                <label
                                    :key="`unit-label-${unit.value}`"
                                    class="btn btn-outline-success"
                                    :for="`unit-${unit.value}`"
                                >
                                    {{ unit.label }}
                                </label>
                            </template>
                        </div>
                    </div>

                    <div class="col-12 col-lg-6">
                        <label for="cari_pasien" class="form-label small fw-semibold text-muted">
                            Cari Pasien
                        </label>
                        <div class="input-group">
                            <span class="input-group-text"><i class="fa fa-search"></i></span>
                            <input
                                id="cari_pasien"
                                type="text"
                                class="form-control"
                                placeholder="No. Registrasi"
                                v-model="filter.cari_pasien"
                                @keyup.enter="getHistoryPasien"
                            />
                        </div>
                    </div>

                    <div class="col-12 col-md-4">
                        <label class="form-label small fw-semibold text-muted">Dokter yang Merawat</label>
                        <v-select
                            v-model="selected.dokter"
                            :options="options.dokter"
                            :filterable="false"
                            label="label"
                            placeholder="Semua dokter"
                            @search="searchDokter"
                            @open="searchDokter('')"
                            @input="filter.dokter_rawat = $event ? $event.value : ''"
                        >
                            <template #no-options>Ketik untuk mencari dokter...</template>
                        </v-select>
                    </div>

                    <div class="col-12 col-md-4">
                        <label class="form-label small fw-semibold text-muted">History Layanan Poli</label>
                        <v-select
                            v-model="selected.poli"
                            :options="options.poli"
                            :filterable="false"
                            label="label"
                            placeholder="Semua poli"
                            @search="searchPoli"
                            @open="searchPoli('')"
                            @input="filter.history_poli = $event ? $event.value : ''"
                        >
                            <template #no-options>Ketik untuk mencari poli...</template>
                        </v-select>
                    </div>

                    <div class="col-12 col-md-4">
                        <label class="form-label small fw-semibold text-muted">History Diagnosa</label>
                        <v-select
                            v-model="selected.diagnosa"
                            :options="options.diagnosa"
                            :filterable="false"
                            label="label"
                            placeholder="Semua diagnosa"
                            @search="searchDiagnosa"
                            @open="searchDiagnosa('')"
                            @input="filter.history_diagnosa = $event ? $event.value : ''"
                        >
                            <template #no-options>Ketik untuk mencari diagnosa...</template>
                        </v-select>
                    </div>
                </div>

                <div class="d-flex justify-content-end gap-2 mt-3">
                    <button class="btn btn-outline-danger" @click="resetFilter" :disabled="is_loading">
                        <i class="fa fa-refresh"></i> Reset
                    </button>
                    <button class="btn btn-primary" @click="getHistoryPasien" :disabled="is_loading">
                        <i class="fa" :class="is_loading ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                        {{ is_loading ? "Memuat..." : "Cari" }}
                    </button>
                </div>
            </div>
        </div>

        <div class="card border-0 shadow-sm">
            <div class="card-header bg-white fw-semibold">
                <i class="fa fa-list-ul text-success me-1"></i> Riwayat Registrasi
            </div>

            <div class="card-body position-relative">
                <div v-if="is_loading" class="table-loading">
                    <div class="spinner-border text-success" role="status"></div>
                    <div class="mt-2 fw-semibold text-muted">Memuat data...</div>
                </div>

                <div class="table-responsive">
                    <table class="table table-hover align-middle mb-0">
                        <thead class="table-light">
                            <tr>
                                <th style="width: 50px">No</th>
                                <th style="width: 180px">Aksi</th>
                                <th>No. Registrasi</th>
                                <th>No. RM</th>
                                <th>Tgl Daftar</th>
                                <th>Nama</th>
                                <th>Diagnosa</th>
                                <th>Poli</th>
                                <th>Ruangan</th>
                                <th>Unit</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in history_data" :key="row.Regno">
                                <td>{{ index + 1 }}</td>
                                <td>
                                    <button class="btn btn-sm btn-primary" @click="openRiwayat(row.Regno)">
                                        <i class="fa fa-folder-open"></i> Riwayat Pelayanan
                                    </button>
                                </td>
                                <td class="fw-semibold">{{ row.Regno }}</td>
                                <td>{{ row.Medrec }}</td>
                                <td>{{ formatTanggal(row.Regdate) }}</td>
                                <td>{{ row.Firstname }}</td>
                                <td>
                                    <span v-if="row.icd10">
                                        {{ row.icd10.KDICD }} - {{ row.icd10.DIAGNOSA }}
                                    </span>
                                    <span v-else class="text-muted">-</span>
                                </td>
                                <td>{{ row.poli ? row.poli.NMPoli : "-" }}</td>
                                <td>
                                    {{ row.fppri && row.fppri.bangsal ? row.fppri.bangsal.NmBangsal : "-" }}
                                </td>
                                <td>
                                    <span
                                        class="badge"
                                        :class="row.fppri ? 'bg-warning text-dark' : 'bg-info text-dark'"
                                    >
                                        {{ row.fppri ? "Rawat Inap" : "Rawat Jalan" }}
                                    </span>
                                </td>
                            </tr>

                            <tr v-if="!is_loading && !history_data.length">
                                <td colspan="10" class="text-center text-muted py-4">
                                    <i class="fa fa-inbox fa-2x d-block mb-2"></i>
                                    Tidak ada riwayat registrasi yang cocok dengan filter.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div class="modal fade" id="modal-riwayat-pelayanan" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-xl modal-dialog-scrollable modal-fullscreen-lg-down">
                <div class="modal-content">
                    <div class="modal-header bg-success text-white">
                        <h5 class="modal-title">
                            <i class="fa fa-folder-open me-1"></i> Riwayat Pelayanan
                            <small v-if="active_regno" class="opacity-75">— No. Reg {{ active_regno }}</small>
                        </h5>
                        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body bg-body-tertiary">
                        <RiwayatPelayananDetail v-if="active_regno" :regno="active_regno" />
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
                            Tutup
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Modal } from "bootstrap";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import RiwayatPelayananDetail from "./RiwayatPelayananDetail.vue";

const defaultFilter = () => ({
    unit_tindakan: 3,
    dokter_rawat: "",
    history_poli: "",
    history_diagnosa: "",
    cari_pasien: "",
});

export default {
    name: "RiwayatPasienDetail",

    components: {
        "v-select": vSelect,
        RiwayatPelayananDetail,
    },

    data() {
        return {
            medrec: this.$route.params.medrec,
            is_loading: false,
            filter: defaultFilter(),
            history_data: [],
            selected: { dokter: null, poli: null, diagnosa: null },
            options: { dokter: [], poli: [], diagnosa: [] },
            active_regno: null,
            modal: null,
            unit_tindakan_options: [
                { value: 1, label: "Rawat Jalan" },
                { value: 2, label: "Rawat Inap" },
                { value: 3, label: "Keduanya" },
            ],
        };
    },

    computed: {
        pasien() {
            const row = this.history_data[0];
            if (!row) return {};

            return {
                nama: row.Firstname,
                sex: row.Sex,
                bod: row.Bod,
            };
        },
    },

    created() {
        this.searchDokter = _.debounce((search) => this.fetchFilter("filter-dokter", search), 350);
        this.searchPoli = _.debounce((search) => this.fetchFilter("filter-poli", search), 350);
        this.searchDiagnosa = _.debounce((search) => this.fetchFilter("filter-diagnosa", search), 350);
    },

    mounted() {
        this.getHistoryPasien();

        this.modal = new Modal(document.getElementById("modal-riwayat-pelayanan"));

        document
            .getElementById("modal-riwayat-pelayanan")
            .addEventListener("hidden.bs.modal", () => (this.active_regno = null));
    },

    beforeDestroy() {
        if (this.modal) this.modal.dispose();
    },

    methods: {
        getHistoryPasien() {
            this.is_loading = true;

            this.axios
                .get("/api/riwayat-pasien/registrasi", {
                    params: { medrec: this.medrec, ...this.filter },
                })
                .then(({ data }) => (this.history_data = data.data || []))
                .catch(() => {
                    this.history_data = [];
                    this.$swal({
                        icon: "error",
                        title: "Gagal memuat data",
                        text: "Riwayat registrasi pasien tidak dapat diambil.",
                    });
                })
                .finally(() => (this.is_loading = false));
        },

        resetFilter() {
            this.filter = defaultFilter();
            this.selected = { dokter: null, poli: null, diagnosa: null };
            this.getHistoryPasien();
        },

        fetchFilter(endpoint, search) {
            const key = { "filter-dokter": "dokter", "filter-poli": "poli", "filter-diagnosa": "diagnosa" }[
                endpoint
            ];

            this.axios
                .get(`/api/riwayat-pasien/${endpoint}`, {
                    params: { medrec: this.medrec, search },
                })
                .then(({ data }) => {
                    this.options[key] = (data.data || []).map((item) => {
                        if (key === "dokter") {
                            return {
                                value: item.KdDoc,
                                label: item.NmPoli ? `${item.NmDoc} (${item.NmPoli})` : item.NmDoc,
                            };
                        }
                        if (key === "poli") {
                            return { value: item.KDPoli, label: item.NMPoli };
                        }
                        return { value: item.KDICD, label: `${item.KDICD} - ${item.DIAGNOSA}` };
                    });
                })
                .catch(() => (this.options[key] = []));
        },

        openRiwayat(regno) {
            this.active_regno = regno;
            this.modal.show();
        },

        formatTanggal(value) {
            if (!value) return "-";
            return moment(String(value)).format("DD MMM YYYY");
        },
    },
};
</script>

<style scoped>
.avatar-circle {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #198754;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
}

.table-loading {
    position: absolute;
    inset: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.75);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 0.375rem;
}
</style>
